import * as React from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { CategoryOperations } from "../module/CategoryOperations";
import {
  ActionCard,
  CheckList,
  DataList,
  DmarcRiskList,
  FormDialog,
  KeyValue,
  MailboxDestPicker,
  MailSyncPanel,
  RelatedLinks,
  StatusPill,
  type CheckItem,
  type DataListColumn,
  type FormFieldSpec,
} from "@ozpanel/kit";
import {
  Button,
  ConfirmDialog,
  Section,
  StateView,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  useAuth,
  useToast,
} from "@ozpanel/ui";

// The mail PLATFORM: whether this node can actually send and receive, what the internet sees of
// it, and the machinery around delivery — journalling, junk learning, quarantine digests,
// migrations, backups.
//
// The domain screens cover per-domain configuration. This is the layer under them, and none of
// it had a console: a node's deliverability identity was checked with dig, its engine health
// with a shell script, and the answer to "why is our mail going to spam" reconstructed by hand
// each time.

// A verdict endpoint's shape, shared by /mail/health and /mail/dovecot/verify.
interface Verdict {
  verdict: string;
  /**
   * COUNTS, not a sentence — /mail/health and /mail/dovecot/verify both send
   * `{pass, warn, fail}`. Declared `string` it was rendered directly as a paragraph, which is an
   * object handed to React as a child: "Objects are not valid as a React child", and the Engine
   * tab went down on load. The three are shown apart deliberately, because a check that could
   * not run is neither a pass nor a failure and this platform has already shipped the bug where
   * an unmeasurable requirement reported OK.
   */
  summary?: { pass?: number; warn?: number; fail?: number } | null;
  consistent?: boolean;
  engine?: string;
  checks: { check: string; status: string; detail: string }[] | null;
}

/** One line for a verdict's counts, so both tabs say it the same way. */
function verdictSummary(s?: { pass?: number; warn?: number; fail?: number } | null): string {
  if (!s) return "";
  const parts = [`${s.pass ?? 0} passed`];
  if (s.warn) parts.push(`${s.warn} to look at`);
  if (s.fail) parts.push(`${s.fail} failing`);
  return parts.join(" · ");
}

// The three states are kept apart deliberately. A check that could not run is not a pass and not
// a failure, and this platform has already shipped the bug where an unmeasurable requirement
// reported OK — so "skip"/"unknown" renders as its own thing rather than being rounded to either.
function toCheckItems(v?: Verdict): CheckItem[] {
  return (v?.checks ?? []).map((c) => ({
    key: c.check,
    label: c.check,
    state: c.status === "pass" ? "ok" : c.status === "fail" ? "fail" : "unknown",
    detail: c.detail,
  }));
}

function VerdictPanel({ title, v, loading, error }: { title: string; v?: Verdict; loading: boolean; error: unknown }) {
  return (
    <Section
      title={title}
      hint={v?.engine}
      bodyClassName="p-0"
      actions={
        v?.verdict ? (
          <StatusPill tone={v.verdict === "ok" || v.verdict === "healthy" ? "ok" : v.verdict === "unknown" ? "warn" : "bad"}>
            {v.verdict}
          </StatusPill>
        ) : undefined
      }
    >
      <StateView loading={loading} error={error} errorContext={title.toLowerCase()}>
        {v?.summary && (
          <div className="border-b border-line px-4 py-2.5 text-[12px] leading-relaxed text-muted">
            {verdictSummary(v.summary)}
          </div>
        )}
        <CheckList items={toCheckItems(v)} summarise emptyMessage="Nothing measured." />
      </StateView>
    </Section>
  );
}

function EngineTab() {
  const { client } = useAuth();
  const health = useQuery({
    queryKey: ["mail-health"],
    queryFn: () => client.apiFetch<Verdict>("/mail/health"),
    refetchInterval: 30000,
  });
  const dovecot = useQuery({
    queryKey: ["mail-dovecot-verify"],
    queryFn: () => client.apiFetch<Verdict>("/mail/dovecot/verify"),
    refetchInterval: 60000,
  });

  return (
    <div className="space-y-4">
      <VerdictPanel title="Mail engine" v={health.data} loading={health.isLoading} error={health.error} />
      <VerdictPanel title="Dovecot" v={dovecot.data} loading={dovecot.isLoading} error={dovecot.error} />

      <Section title="Actions">
        <ActionCard
          title="Migrate a mailbox home"
          description="Moves a tenant's mail to the shared volume and repoints Dovecot at it. Reads back what Dovecot resolves for the user afterwards rather than assuming the move took — the delivery uid is the tenant's own, and a home the tenant cannot traverse looks identical to an empty mailbox."
          actionLabel="Migrate"
          danger="high"
          confirm={{
            title: "Migrate a mailbox home?",
            body: "Mail is moved on disk. A mount that is absent rather than broken accepts writes onto local disk where no other node can see them, so the destination is verified as a real shared mount before anything is copied.",
            confirmLabel: "Migrate",
          }}
          run={() => client.apiFetch("/mail/dovecot/migrate-home", { method: "POST" })}
          invalidates={[["mail-dovecot-verify"]]}
        />
      </Section>
    </div>
  );
}

// ── Deliverability: what the internet sees ────────────────────────────────────────────────

interface DNSRecord {
  name: string;
  type: string;
  content: string;
  present: boolean;
  actual?: string;
  owner?: string;
}
interface Identity {
  helo: string;
  sending_ip: string;
  zone_is_ours: boolean;
  // A SENTENCE here — "2 of 3 identity records are missing or wrong." — unlike Verdict.summary,
  // which is counts. Two endpoints, one field name, two meanings; both are typed as they are
  // rather than one being assumed from the other.
  summary: string;
  note?: string;
  /** A COUNT of the records that are missing or wrong. It was declared as the list of them. */
  missing?: number;
  records: DNSRecord[] | null;
}

function IdentityTab() {
  const { client } = useAuth();
  const id = useQuery({
    queryKey: ["mail-identity"],
    queryFn: () => client.apiFetch<Identity>("/mail/identity"),
    refetchInterval: 60000,
  });
  const ips = useQuery({
    queryKey: ["mail-node-ips"],
    queryFn: () =>
      client.apiFetch<{ ips: string[] | null; default: string; not_publishable: string[] | null; note?: string }>(
        "/mail/node-ips",
      ),
  });

  const cols: DataListColumn<DNSRecord>[] = [
    { key: "name", label: "Name", sortable: true, render: (r) => <span className="font-mono text-[12px]">{r.name}</span> },
    { key: "type", label: "Type", sortable: true },
    {
      key: "present",
      label: "Published",
      sortable: true,
      // present=false is the actionable state and must not be a blank cell.
      render: (r) => <StatusPill tone={r.present ? "ok" : "bad"}>{r.present ? "yes" : "missing"}</StatusPill>,
    },
    { key: "content", label: "Expected", render: (r) => <span className="font-mono text-[11.5px]">{r.content}</span> },
    {
      key: "actual",
      label: "Actually published",
      // Showing both is the point. A record that exists with the WRONG value fails exactly like
      // a missing one, and only the comparison distinguishes them.
      render: (r) =>
        r.actual ? (
          <span className={`font-mono text-[11.5px] ${r.actual === r.content ? "" : "text-crit"}`}>{r.actual}</span>
        ) : (
          <span className="text-muted">nothing</span>
        ),
    },
    { key: "owner", label: "Zone", render: (r) => <span className="text-[11.5px] text-muted">{r.owner ?? "—"}</span> },
  ];

  return (
    <div className="space-y-4">
      <Section title="Sending identity" bodyClassName="p-0">
        <StateView loading={id.isLoading} error={id.error} errorContext="the sending identity">
          <KeyValue
            rows={[
              {
                key: "helo",
                label: "HELO name",
                value: id.data?.helo,
                hint: "What this node calls itself when it connects to another mail server. It must resolve, and its reverse DNS must come back to it — receivers check both, and a mismatch is scored against every message.",
              },
              {
                key: "sending_ip",
                label: "Sending address",
                value: id.data?.sending_ip,
                hint: "The address outbound mail leaves from. Reverse DNS is set by whoever owns the IP — usually the host, not this panel.",
              },
              {
                key: "zone_is_ours",
                label: "We host this zone",
                value: id.data?.zone_is_ours,
                goodWhen: true,
                hint: "When the zone is elsewhere, the records below must be published by hand wherever it lives. Nothing here can add them.",
              },
              { key: "default", label: "Default outbound IP", value: ips.data?.default },
              {
                key: "not_publishable",
                label: "Not publishable",
                value: ips.data?.not_publishable ?? [],
                hint: "Addresses that exist on this node but must never appear in DNS — private ranges, mesh addresses. Publishing one sends receivers to an address they cannot reach.",
              },
            ]}
          />
        </StateView>
        {id.data?.summary && (
          <div className="border-t border-line px-4 py-2.5 text-[12px] leading-relaxed">{id.data.summary}</div>
        )}
        {id.data?.note && (
          <div className="border-t border-line px-4 py-2.5 text-[12px] leading-relaxed text-muted">{id.data.note}</div>
        )}
      </Section>

      <Section
        title="Records the internet needs to see"
        hint={id.data?.missing ? `${id.data.missing} missing or wrong` : undefined}
        bodyClassName="p-0"
      >
        <div className="p-4">
          <DataList
            items={id.data?.records ?? []}
            columns={cols}
            getRowKey={(r) => `${r.type}:${r.name}`}
            loading={id.isLoading}
            error={id.error}
            errorContext="the identity records"
            emptyMessage="No records expected."
          />
        </div>
      </Section>

      <Section title="Actions">
        <ActionCard
          title="Publish the missing records"
          description="Writes the records above into the zones this node manages. Records in zones hosted elsewhere are reported rather than published, because nothing here can write them — and reporting them is the only way anyone learns they are the reason mail is being rejected."
          actionLabel="Publish"
          danger="medium"
          confirm={{
            title: "Publish this node's identity records?",
            body: "Only zones this node manages are touched. Existing records with different values are corrected.",
          }}
          run={() => client.apiFetch("/mail/identity/publish", { method: "POST" })}
          invalidates={[["mail-identity"]]}
        />
      </Section>
    </div>
  );
}

// ── Domain consistency ────────────────────────────────────────────────────────────────────

function ConsistencyTab() {
  const { client } = useAuth();
  const q = useQuery({
    queryKey: ["mail-verify"],
    queryFn: () =>
      client.apiFetch<{
        consistent: boolean;
        items: { domain: string; ok: boolean; registered: boolean; detail?: string }[] | null;
        orphans: string[] | null;
        records?: unknown;
      }>("/mail/verify"),
  });

  const items: CheckItem[] = (q.data?.items ?? []).map((d) => ({
    key: d.domain,
    label: d.domain,
    state: d.ok ? "ok" : "fail",
    detail: d.detail ?? (d.registered ? "registered" : "not registered in the platform's records"),
    because: d.ok
      ? undefined
      : "Mail for this domain may be accepted and then have nowhere to go, or be rejected at the door. The two look very different to a sender and identical from here.",
  }));

  return (
    <div className="space-y-4">
      <Section
        title="Domains"
        bodyClassName="p-0"
        actions={
          q.data ? (
            <StatusPill tone={q.data.consistent ? "ok" : "bad"}>
              {q.data.consistent ? "consistent" : "inconsistent"}
            </StatusPill>
          ) : undefined
        }
      >
        <StateView loading={q.isLoading} error={q.error} errorContext="the domain check">
          <CheckList items={items} summarise emptyMessage="No mail domains." />
        </StateView>
      </Section>

      {(q.data?.orphans?.length ?? 0) > 0 && (
        <Section title="Orphans" bodyClassName="p-0">
          <div className="px-4 py-3 text-[12px] leading-relaxed text-muted">
            Configured in the mail system but absent from the platform's records. Not necessarily
            wrong, but nothing here owns them — so nothing renews their certificates, publishes
            their DNS or includes them in a backup.
            <div className="mt-2 flex flex-wrap gap-1.5">
              {q.data?.orphans?.map((o) => (
                <span key={o} className="rounded border border-line bg-bg2 px-1.5 py-0.5 font-mono text-[11px]">{o}</span>
              ))}
            </div>
          </div>
        </Section>
      )}
    </div>
  );
}

// ── Delivery machinery ────────────────────────────────────────────────────────────────────

// mail_backup's job row.
interface BackupJob {
  id: number | string; name: string; kind: string; scope: string; target?: string;
  dest_path: string; remote?: string; schedule: string; active: boolean;
  last_run_at?: string; last_status?: string; last_detail?: string;
}

// The create form for POST /mail/backups (mail_backup.go validMailBackup decides what is valid).
function backupFields(defaultDest?: string): FormFieldSpec[] {
  return [
    { key: "name", label: "Name", required: true, placeholder: "nightly" },
    { key: "kind", label: "Kind", type: "enum", defaultValue: "backup",
      options: [{ value: "backup", label: "backup — a copy on this cluster's disk" }, { value: "sync", label: "sync — with a remote node" }] },
    { key: "scope", label: "Covers", type: "enum", defaultValue: "all",
      options: [{ value: "all", label: "every mailbox" }, { value: "domain", label: "one domain" }, { value: "mailbox", label: "one mailbox" }] },
    { key: "target", label: "Domain or address", help: "Required when it covers one domain or one mailbox." },
    { key: "dest_path", label: "Writes to", defaultValue: defaultDest ?? "", help: "An absolute path for a backup; a local, non-shared disk." },
    { key: "remote", label: "Remote", help: "For a sync: the doveadm remote it reconciles with." },
    { key: "schedule", label: "Schedule", placeholder: "17 3 * * *",
      help: "Five cron fields or @daily / @weekly / @hourly. Empty = run only on demand." },
  ];
}

function MachineryTab() {
  const qc = useQueryClient();
  const { notify } = useToast();
  const backupCols: DataListColumn<BackupJob>[] = [
    { key: "name", label: "Job", sortable: true },
    { key: "scope", label: "Covers", render: (b) => (b.scope === "all" ? "every mailbox" : `${b.scope}: ${b.target || "—"}`) },
    { key: "dest_path", label: "Writes to", render: (b) => <span className="font-mono text-[12px]">{b.remote || b.dest_path}</span> },
    { key: "schedule", label: "When", render: (b) => <span className="font-mono text-[12px]">{b.schedule}</span> },
    {
      key: "last", label: "Last run", sortable: true, value: (b) => b.last_run_at || "",
      // A backup job that is configured, scheduled and FAILING looks identical to a healthy one
      // unless the last result is shown. That is the only column here that can tell you your mail
      // is not actually being backed up.
      render: (b) =>
        b.last_run_at ? (
          <span className="text-[12px]">
            <StatusPill tone={b.last_status === "ok" || b.last_status === "success" ? "ok" : "bad"}>{b.last_status || "?"}</StatusPill>
            <span className="ml-2 text-muted">{b.last_run_at}</span>
          </span>
        ) : (
          <span className="text-[12px] text-warn">never run</span>
        ),
    },
    {
      // Scheduled = active AND has a schedule: only then is it in the workload primary's cron. A job
      // with no schedule runs only on "Run now", whatever its switch says.
      key: "active", label: "Schedule", sortable: true, value: (b) => (b.active ? 1 : 0),
      render: (b) =>
        !b.schedule ? <StatusPill tone="neutral">on demand</StatusPill>
          : b.active ? <StatusPill tone="ok">enabled</StatusPill>
          : <StatusPill tone="warn">paused</StatusPill>,
    },
  ];

  const { client } = useAuth();
  const msg = (e: unknown) => (e instanceof Error ? e.message : String(e));
  const refresh = () => void qc.invalidateQueries({ queryKey: ["mail-backups"] });

  const runBackup = (b: BackupJob) =>
    client
      .apiFetch(`/mail/backups/${encodeURIComponent(String(b.id))}/run`, { method: "POST" })
      .then(() => {
        notify({ kind: "success", title: `${b.name} started` });
        refresh();
      })
      .catch((e: unknown) => notify({ kind: "error", title: "Could not start it", body: msg(e) }));

  // POST /mail/backups/{id}/active — enabling puts the job in the workload primary's cron at once.
  const setActive = (b: BackupJob, active: boolean) =>
    client
      .apiFetch(`/mail/backups/${encodeURIComponent(String(b.id))}/active`, { method: "POST", body: JSON.stringify({ active }) })
      .then(() => {
        notify({ kind: "success", title: active ? `${b.name} enabled` : `${b.name} paused` });
        refresh();
      })
      .catch((e: unknown) => notify({ kind: "error", title: active ? "Could not enable it" : "Could not pause it", body: msg(e) }));

  const [creating, setCreating] = React.useState(false);
  const [createError, setCreateError] = React.useState<string | null>(null);
  const [savingNew, setSavingNew] = React.useState(false);
  const [removing, setRemoving] = React.useState<BackupJob | null>(null);

  const createBackup = (v: Record<string, unknown>) => {
    setSavingNew(true);
    setCreateError(null);
    const body = {
      name: String(v.name ?? "").trim(), kind: String(v.kind || "backup"), scope: String(v.scope || "all"),
      target: String(v.target ?? "").trim(), dest_path: String(v.dest_path ?? "").trim(),
      remote: String(v.remote ?? "").trim(), schedule: String(v.schedule ?? "").trim(),
    };
    client
      .apiFetch("/mail/backups", { method: "POST", body: JSON.stringify(body) })
      .then(() => {
        setCreating(false);
        notify({ kind: "success", title: `${body.name} created, paused`, body: "Enable it to start its schedule." });
        refresh();
      })
      .catch((e: unknown) => setCreateError(msg(e)))
      .finally(() => setSavingNew(false));
  };
  const journal = useQuery({
    queryKey: ["mail-journal"],
    queryFn: () =>
      client.apiFetch<{ active: boolean; live: unknown; rules: unknown; note?: string; what_it_does?: string }>(
        "/mail/journal",
      ),
  });
  const junk = useQuery({
    queryKey: ["mail-junk-learning"],
    queryFn: () => client.apiFetch<{ enabled: boolean | null; config: unknown; note?: string; detail?: string }>("/mail/junk-learning"),
  });
  const digest = useQuery({
    queryKey: ["mail-digest-schedule"],
    queryFn: () =>
      // `hour` is a STRING ("7") and `recipients` is a SENTENCE describing who gets a digest —
      // "each mailbox owner, for their own held mail". It was declared `string[]`, so the screen
      // treated a prose explanation as a list of addresses.
      client.apiFetch<{ enabled: boolean; hour: string; cron: string; recipients: string; default?: boolean }>(
        "/mail/quarantine/digest/schedule",
      ),
  });
  const backups = useQuery({
    queryKey: ["mail-backups"],
    queryFn: () => client.apiFetch<{ jobs: BackupJob[] | null; default_dest?: string }>("/mail/backups"),
  });

  return (
    <div className="space-y-4">
      <Section
        title="Journalling"
        bodyClassName="p-0"
        actions={<StatusPill tone={journal.data?.active ? "ok" : "neutral"}>{journal.data?.active ? "active" : "off"}</StatusPill>}
      >
        <StateView loading={journal.isLoading} error={journal.error} errorContext="journalling">
          <div className="px-4 py-3 text-[12px] leading-relaxed text-muted">
            {journal.data?.what_it_does ??
              "Copies mail to a nominated address for retention. Its state is read from the running Postfix rather than from our own records — an always_bcc pointing at a map file with no entries reports as configured and copies nothing."}
          </div>
          {journal.data?.note && (
            <div className="border-t border-line px-4 py-2.5 text-[12px] leading-relaxed text-muted">{journal.data.note}</div>
          )}
        </StateView>
      </Section>

      <Section
        title="Junk learning"
        bodyClassName="p-0"
        actions={<StatusPill tone={junk.data?.enabled ? "ok" : "neutral"}>{junk.data?.enabled == null && junk.data ? "unknown" : junk.data?.enabled ? "enabled" : "off"}</StatusPill>}
      >
        <StateView loading={junk.isLoading} error={junk.error} errorContext="junk learning">
          <div className="px-4 py-3 text-[12px] leading-relaxed text-muted">
            {junk.data?.note ??
              "Trains the spam filter from what users move into and out of their Junk folder."}
            {junk.data?.detail && <div className="mt-1.5">{junk.data.detail}</div>}
          </div>
        </StateView>
        <div className="border-t border-line p-4">
          <ActionCard
            title={junk.data?.enabled ? "Re-apply junk learning" : "Enable junk learning"}
            description="Installs the Sieve and Rspamd pieces that learn from a user moving mail into or out of Junk. Idempotent — re-running repairs a half-configured node rather than duplicating anything."
            actionLabel={junk.data?.enabled ? "Re-apply" : "Enable"}
            danger="low"
            run={() => client.apiFetch("/mail/junk-learning/enable", { method: "POST" })}
            invalidates={[["mail-junk-learning"]]}
          />
        </div>
      </Section>

      <Section
        title="Quarantine digest"
        bodyClassName="p-0"
        actions={<StatusPill tone={digest.data?.enabled ? "ok" : "neutral"}>{digest.data?.enabled ? "scheduled" : "off"}</StatusPill>}
      >
        <StateView loading={digest.isLoading} error={digest.error} errorContext="the digest schedule">
          <KeyValue
            rows={[
              { key: "enabled", label: "Enabled", value: digest.data?.enabled, goodWhen: true },
              { key: "hour", label: "Sends at", value: digest.data?.hour == null ? null : `${digest.data.hour}:00` },
              {
                key: "cron",
                label: "Cron entry",
                value: digest.data?.cron,
                hint: "The digest is driven by cron, not by the panel process. A schedule with no cron entry records intent and sends nothing — which is why the entry itself is shown rather than the setting.",
              },
              { key: "recipients", label: "Recipients", value: digest.data?.recipients ?? [] },
            ]}
          />
        </StateView>
      </Section>

      {/* Both of these were JSON.stringify dumps. A pre block of raw JSON is not a screen: it
          cannot be sorted, it cannot be acted on, and it shows internal fields nobody needs while
          burying the two that matter (is it running, did it fail). */}
      {/* Mail domains a quarantine/reject DMARC policy may catch (dmarc.go) — shown, never skipped. */}
      <DmarcRiskList />

      {/* Every mailbox's imports and syncs, platform-wide — the same panel tenants and mailbox users see. */}
      <MailSyncPanel audience="tenant" showNodes title="Mail imports & syncs" destPicker={(v, set) => <MailboxDestPicker value={v} set={set} />} />

      <Section
        title="Mail backups"
        hint={backups.data?.default_dest}
        bodyClassName="p-0"
        actions={<Button size="sm" onClick={() => { setCreateError(null); setCreating(true); }}>New job</Button>}
      >
        <div className="p-4">
          <p className="mb-3 text-[12.5px] text-muted">
            A new job is created paused. An enabled job runs on its schedule on the workload primary only, and
            its copy stays on that node. Each scheduled job is listed with its state under Background tasks
            as <span className="font-mono">mail.backup.&lt;id&gt;</span>.
          </p>
          <DataList
            items={backups.data?.jobs ?? []}
            columns={backupCols}
            getRowKey={(b) => String(b.id)}
            loading={backups.isLoading}
            error={backups.error}
            errorContext="mail backups"
            emptyMessage="No mail backup job. Nothing backs up mail until one is created and enabled."
            rowActions={(b) => (
              <div className="flex gap-1">
                {b.schedule ? (
                  <Button size="sm" variant="ghost" onClick={() => void setActive(b, !b.active)}>
                    {b.active ? "Pause" : "Enable"}
                  </Button>
                ) : null}
                <Button size="sm" variant="ghost" onClick={() => void runBackup(b)}>Run now</Button>
                <Button size="sm" variant="ghost" onClick={() => setRemoving(b)}>Remove</Button>
              </div>
            )}
          />
          <RelatedLinks
            links={[{ label: "Background tasks", to: "/modules/schedule", hint: "each scheduled mail backup job: ok, failing, late or never run" }]}
          />
        </div>
      </Section>
      <FormDialog
        open={creating}
        onOpenChange={(o) => { setCreating(o); if (!o) setCreateError(null); }}
        title="New mail backup job"
        description="Created paused: nothing runs until you enable it. A scheduled job runs on the workload primary."
        submitLabel="Create"
        pending={savingNew}
        error={createError}
        fields={backupFields(backups.data?.default_dest)}
        onSubmit={createBackup}
      />
      <ConfirmDialog
        open={!!removing}
        onOpenChange={(o) => !o && setRemoving(null)}
        title={`Remove ${removing?.name ?? "this job"}?`}
        confirmLabel="Remove"
        body={<p>The job and its schedule are removed. Copies it already wrote stay where they are.</p>}
        onConfirm={() => {
          const b = removing;
          setRemoving(null);
          if (!b) return;
          void client
            .apiFetch(`/mail/backups/${encodeURIComponent(String(b.id))}`, { method: "DELETE" })
            .then(() => { notify({ kind: "success", title: `${b.name} removed` }); refresh(); })
            .catch((e: unknown) => notify({ kind: "error", title: "Could not remove it", body: msg(e) }));
        }}
      />

    </div>
  );
}


// ── Reporting & limits ─────────────────────────────────────────────────────────────────────
//
// GET /rspamd/dmarc and GET /rspamd/ratelimit both answered 200 and were reached by NOTHING —
// two fully-built surfaces with no caller, which is this codebase's dominant defect.
//
// The DMARC one is the canonical INTERLOCK case CLAUDE.md names: `dmarc { reporting }` with no
// cron records for ever and sends nothing, while reporting itself enabled. The endpoint already
// answers it honestly as THREE separate facts — configured, scheduled, sending — so this screen
// shows all three rather than collapsing them into one green pill. A reporting status that says
// "on" without evidence of delivery is the exact bug the standing rules were written for.

interface DmarcView {
  dmarc?: {
    configured?: boolean;
    scheduled?: boolean;
    sending?: boolean;
    from?: string;
    org?: string;
    schedule?: string;
    pending_reports?: number;
    detail?: string;
  } | null;
  note?: string;
  what_it_is?: string;
}

interface RatelimitBucket {
  key?: string;
  name?: string;
  rate?: string;
  protects?: string;
  risk?: string;
}
interface RatelimitView {
  active?: number;
  buckets?: RatelimitBucket[] | null;
  note?: string;
  /** The state sentence the server composes, including the "limits nothing" case. */
  state?: string;
  max_rcpt?: number;
  /** Generous starting values the server suggests, so "on" does not mean "guess a number". */
  defaults?: Record<string, string> | null;
  /** Limits Rspamd is enforcing that the panel does not name — set by hand, and about to be
   *  overwritten the next time we write the file. Shown so that is a decision, not a surprise. */
  unmanaged?: string[] | null;
}

// RatelimitEditor is the WRITE half of the rate limits, and it exists because the read half on
// its own could only ever report the failure it was looking at.
//
// CLAUDE.md's first standing rule opens with this exact case: "Rspamd `ratelimit` with
// `enabled = true` and no `rates` block — limits nothing." The module was loaded, the panel said
// so, and the panel could not set a single rate — so the only state the console could produce was
// the canonical example of a setting that reports itself active while doing nothing. Reading the
// live configuration was already right; it just had nowhere to go once it told you the truth.
//
// The suggested values come from the SERVER (`defaults`), not from a number invented here.
// rspamd/ratelimit.go explains why they are generous: "A limit that defers real mail creates a
// support ticket and an operator who switches the whole feature off, which protects nobody."
function RatelimitEditor({ view, onSaved }: { view?: RatelimitView; onSaved: () => void }) {
  const { client } = useAuth();
  const buckets = view?.buckets ?? [];
  const [draft, setDraft] = React.useState<Record<string, string> | null>(null);
  const [cap, setCap] = React.useState<string | null>(null);
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState<string | null>(null);

  const rates = draft ?? Object.fromEntries(buckets.map((b) => [String(b.key), b.rate ?? ""]));
  const maxRcpt = cap ?? String(view?.max_rcpt ?? 0);
  const dirty = draft !== null || cap !== null;

  const save = async () => {
    setBusy(true);
    setErr(null);
    try {
      await client.apiFetch("/rspamd/ratelimit", {
        method: "PUT",
        body: JSON.stringify({ rates, max_rcpt: Number(maxRcpt) || 0 }),
      });
      setDraft(null);
      setCap(null);
      onSaved();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* The server's own sentence, which says "limits nothing" when that is the case. Kept as
          the server's words rather than re-derived from a count here: two places deciding what
          "active" means is how the two disagree. */}
      {view?.state && (
        <StatusPill tone={(view.active ?? 0) > 0 ? "ok" : "warn"}>{view.state}</StatusPill>
      )}

      <div className="space-y-3">
        {buckets.map((b) => (
          <div key={String(b.key)} className="rounded-lg border border-line p-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="text-[13px] font-medium">{b.name ?? b.key}</span>
              <input
                value={rates[String(b.key)] ?? ""}
                placeholder={view?.defaults?.[String(b.key)] ?? "500 / 1h"}
                onChange={(e) => setDraft({ ...rates, [String(b.key)]: e.target.value })}
                className="w-36 rounded-lg border border-line2 bg-bg px-3 py-1.5 font-mono text-[12.5px] text-fg"
              />
            </div>
            <p className="mt-1 text-[12px] leading-relaxed text-muted">{b.protects}</p>
            {/* The cost of setting it too low, next to the box where you set it — not in a
                separate column somebody reads after the mistake. */}
            {b.risk && <p className="mt-1 text-[11.5px] leading-relaxed text-muted">If set too low: {b.risk}</p>}
          </div>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <label className="text-[12.5px] text-muted">Recipients per message</label>
        <input
          value={maxRcpt}
          onChange={(e) => setCap(e.target.value)}
          className="w-24 rounded-lg border border-line2 bg-bg px-3 py-1.5 font-mono text-[12.5px] text-fg"
        />
        <span className="text-[11.5px] text-muted">0 = no cap</span>
      </div>

      <p className="max-w-3xl text-[11.5px] leading-relaxed text-muted">
        An empty box means that limit is not applied. Clearing every box switches rate limiting
        off entirely — the module stays loaded and restricts nothing, which is the state that
        reads as enabled while doing nothing.
      </p>

      {view?.unmanaged && view.unmanaged.length > 0 && (
        <div className="rounded-lg border border-warn/40 bg-warn/5 p-3">
          <p className="text-[12px] leading-relaxed">
            Rspamd is also enforcing limits this panel does not manage. Saving here REPLACES the
            file and removes them:
          </p>
          <ul className="mt-1 font-mono text-[11.5px] text-muted">
            {view.unmanaged.map((u) => (
              <li key={u}>{u}</li>
            ))}
          </ul>
        </div>
      )}

      {err && <p className="text-[12px] text-crit">{err}</p>}

      <div className="flex items-center gap-2">
        <Button size="sm" onClick={() => void save()} disabled={busy || !dirty}>
          {busy ? "Saving…" : "Save limits"}
        </Button>
        {view?.defaults && (
          <Button size="sm" variant="ghost" onClick={() => setDraft({ ...view.defaults })}>
            Use suggested values
          </Button>
        )}
        {dirty && (
          <Button size="sm" variant="ghost" onClick={() => { setDraft(null); setCap(null); }}>
            Discard
          </Button>
        )}
      </div>

      {view?.note && <p className="max-w-3xl text-[12px] leading-relaxed text-muted">{view.note}</p>}
    </div>
  );
}

function ReportingTab() {
  const { client } = useAuth();
  const dmarc = useQuery({
    queryKey: ["rspamd-dmarc"],
    queryFn: () => client.apiFetch<DmarcView>("/rspamd/dmarc"),
    retry: false,
  });
  const limits = useQuery({
    queryKey: ["rspamd-ratelimit"],
    queryFn: () => client.apiFetch<RatelimitView>("/rspamd/ratelimit"),
    retry: false,
  });

  const d = dmarc.data?.dmarc ?? {};
  // "Sending" is the only one that means reports leave this node. The other two are
  // preconditions, and showing them separately is the point — a green "enabled" over a dead cron
  // is what this endpoint exists to make impossible.
  const steps: [string, boolean | undefined, string][] = [
    ["Rspamd records the data", d.configured, "Without this there is nothing to report on."],
    ["A job is scheduled", d.scheduled, "Configuration alone sends nothing — this is the half that is usually missing."],
    ["Reports are being sent", d.sending, "The only one that means a domain owner actually hears from us."],
  ];

  const cols: DataListColumn<RatelimitBucket>[] = [
    { key: "name", label: "Limit", sortable: true, render: (b) => <span className="font-medium">{b.name ?? b.key}</span> },
    { key: "rate", label: "Rate", render: (b) => <span className="font-mono text-[12.5px]">{b.rate ?? "—"}</span> },
    { key: "protects", label: "What it protects", render: (b) => <span className="text-[12px] text-muted">{b.protects}</span> },
    { key: "risk", label: "If set too low", render: (b) => <span className="text-[12px] text-muted">{b.risk}</span> },
  ];

  return (
    <div className="space-y-4">
      <Section title="DMARC aggregate reporting">
        <StateView loading={dmarc.isLoading} error={dmarc.error} errorContext="DMARC reporting">
          <div className="space-y-3">
            {dmarc.data?.what_it_is && (
              <p className="max-w-3xl text-[12px] leading-relaxed text-muted">{dmarc.data.what_it_is}</p>
            )}
            <div className="space-y-2">
              {steps.map(([label, ok, why]) => (
                <div key={label} className="flex items-start gap-3">
                  <StatusPill tone={ok === undefined ? "neutral" : ok ? "ok" : "bad"}>
                    {ok === undefined ? "unknown" : ok ? "yes" : "no"}
                  </StatusPill>
                  <span className="min-w-0">
                    <span className="block text-[13px]">{label}</span>
                    <span className="block text-[11.5px] text-muted">{why}</span>
                  </span>
                </div>
              ))}
            </div>
            {d.detail && <p className="text-[12px] text-muted">{d.detail}</p>}
            {d.pending_reports !== undefined && (
              <p className="text-[12px] text-muted">Reports waiting to go out: {d.pending_reports}</p>
            )}
            {dmarc.data?.note && (
              <p className="max-w-3xl rounded-lg border border-line bg-surface2 px-3 py-2 text-[12px] leading-relaxed text-muted">
                {dmarc.data.note}
              </p>
            )}
          </div>
        </StateView>
      </Section>

      <Section title="Rate limits" bodyClassName="p-0">
        <StateView loading={limits.isLoading} error={limits.error} errorContext="rate limits">
          <div className="p-4">
            {/* Read from the RUNNING configuration, not from what we stored. mail_ratelimit.go's
                own comment records that these limits once CLAIMED to be enforced when they were
                not, so the only honest source is what Rspamd is actually configured with. */}
            <RatelimitEditor view={limits.data} onSaved={() => void limits.refetch()} />
          </div>
        </StateView>
      </Section>
    </div>
  );
}


// ── Mail flow ──────────────────────────────────────────────────────────────────────────────
//
// How much mail moved and what happened to it, over time. The platform could not answer this at
// all (OPEN-ITEMS.md §1b): the queue is an INSTANT — a healthy server and one that just sent a
// million messages both read 0 — Rspamd's counters are cumulative since its process started and
// reset on restart, and usage web bandwidth comes from Caddy, i.e. web traffic.

interface FlowDay {
  day: string;
  sent?: number;
  bounced?: number;
  deferred?: number;
  rejected?: number;
  spam?: number;
  // NOTE: the endpoint also sends <outcome>_bytes per day. Not declared here because nothing
  // renders it yet, and a field a console asserts but never reads is a claim nobody checks —
  // TestConsoleFieldNamesExistInGoStructs flagged exactly that, since the Go side builds the
  // name by concatenation (outcome+"_bytes") and a presence scan cannot see it. When bytes are
  // shown, the field comes back with the justification, not before.
}
interface FlowView {
  days?: number;
  series?: FlowDay[] | null;
  totals?: Record<string, number>;
  nodes?: string[] | null;
  since?: string;
  note?: string;
}

const OUTCOMES: { key: keyof FlowDay; label: string; tone: string; what: string }[] = [
  { key: "sent", label: "Delivered", tone: "text-ok", what: "Accepted and handed to the destination." },
  { key: "rejected", label: "Refused", tone: "text-warn", what: "Turned away at SMTP time — the sender was told, and it never entered the queue." },
  { key: "spam", label: "Spam", tone: "text-warn", what: "Refused by the spam filter specifically." },
  { key: "deferred", label: "Deferred", tone: "text-muted", what: "Not delivered yet, and may still succeed." },
  { key: "bounced", label: "Bounced", tone: "text-crit", what: "Accepted, then failed. We took responsibility and had to give it back." },
];

function FlowTab() {
  const { client } = useAuth();
  const [days, setDays] = React.useState(30);
  const q = useQuery({
    queryKey: ["mail-flow", days],
    queryFn: () => client.apiFetch<FlowView>(`/mail/flow?days=${days}`),
    retry: false,
  });
  const d = q.data;
  const series = d?.series ?? [];
  const peak = Math.max(1, ...series.map((r) => OUTCOMES.reduce((n, o) => n + ((r[o.key] as number) ?? 0), 0)));

  return (
    <div className="space-y-4">
      <Section
        title="Mail flow"
        actions={
          <div className="flex gap-1">
            {[7, 30, 90].map((n) => (
              <Button key={n} size="sm" variant={days === n ? "accent" : "ghost"} onClick={() => setDays(n)}>
                {n}d
              </Button>
            ))}
          </div>
        }
      >
        <StateView loading={q.isLoading} error={q.error} errorContext="mail flow">
          <div className="space-y-4">
            <div className="flex flex-wrap gap-5">
              {OUTCOMES.map((o) => (
                <div key={o.key} className="min-w-[8rem]">
                  <div className={`text-[20px] ${o.tone}`}>{d?.totals?.[o.key as string] ?? 0}</div>
                  <div className="text-[11.5px] text-muted">{o.label}</div>
                  <div className="mt-0.5 max-w-[13rem] text-[11px] leading-snug text-faint">{o.what}</div>
                </div>
              ))}
            </div>

            {/* A bar per day. Deliberately not a smoothed line: these are counts of discrete
                events, and a day with no ROW is not a day with no mail — it is a day before
                collection started, which the note below says out loud. */}
            <div className="flex items-end gap-[3px] overflow-x-auto pb-1" style={{ height: 120 }}>
              {series.map((r) => {
                const total = OUTCOMES.reduce((n, o) => n + ((r[o.key] as number) ?? 0), 0);
                return (
                  <div
                    key={r.day}
                    title={`${r.day}: ${OUTCOMES.map((o) => `${o.label} ${(r[o.key] as number) ?? 0}`).join(", ")}`}
                    className="flex w-[10px] shrink-0 flex-col justify-end"
                    style={{ height: "100%" }}
                  >
                    {OUTCOMES.map((o) => {
                      const v = (r[o.key] as number) ?? 0;
                      if (!v) return null;
                      return (
                        <div
                          key={o.key}
                          className={
                            o.key === "sent" ? "bg-ok" : o.key === "bounced" ? "bg-crit" : o.key === "deferred" ? "bg-line2" : "bg-warn"
                          }
                          style={{ height: `${(v / peak) * 100}%` }}
                        />
                      );
                    })}
                    {total === 0 && <div className="bg-line" style={{ height: 2 }} />}
                  </div>
                );
              })}
              {series.length === 0 && (
                <p className="text-[12.5px] text-muted">Nothing recorded yet — collection starts with the first pass after this node was upgraded.</p>
              )}
            </div>

            {/* WHICH NODES, always. Each node folds only its own journal, so a total that does
                not say what it covers is the honest-looking version of a wrong answer. */}
            <div className="space-y-1 text-[11.5px] text-muted">
              {d?.nodes && d.nodes.length > 0 && <div>Counted from: {d.nodes.join(", ")}</div>}
              {d?.since && <div>Recording since {d.since}.</div>}
              {d?.note && <div className="max-w-3xl leading-relaxed">{d.note}</div>}
            </div>
          </div>
        </StateView>
      </Section>
      <CategoryOperations category="mail" title="Mail operations" hint="Postfix, Dovecot, Rspamd and the mail-domain data all three read." />

    </div>
  );
}

export function MailAdmin() {
  return (
    <Tabs defaultValue="flow">
      <TabsList>
        <TabsTrigger value="flow">Flow</TabsTrigger>
        <TabsTrigger value="engine">Engine</TabsTrigger>
        <TabsTrigger value="identity">Deliverability</TabsTrigger>
        <TabsTrigger value="consistency">Domains</TabsTrigger>
        <TabsTrigger value="machinery">Delivery machinery</TabsTrigger>
        <TabsTrigger value="reporting">Reporting &amp; limits</TabsTrigger>
      </TabsList>
      <TabsContent value="flow">
        <FlowTab />
      </TabsContent>
      <TabsContent value="engine">
        <EngineTab />
      </TabsContent>
      <TabsContent value="identity">
        <IdentityTab />
      </TabsContent>
      <TabsContent value="consistency">
        <ConsistencyTab />
      </TabsContent>
      <TabsContent value="machinery">
        <MachineryTab />
      </TabsContent>
      <TabsContent value="reporting">
        <ReportingTab />
      </TabsContent>
    </Tabs>
  );
}
